/**
 * Draft state for a PRP form: local edits, dirty tracking, and save with the
 * document's version (optimistic locking on the server).
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mutationMeta } from "@/lib/query-client";
import { PRP_DOC_LABELS, updatePrpDocument, type PrpDocument } from "@/api/prp.api";

export function useDraft<T>(doc: PrpDocument, parse: (raw?: string) => T, onDirtyChange: (dirty: boolean) => void) {
  const qc = useQueryClient();
  const saved = useMemo(() => parse(doc.content), [doc.content, parse]);
  const [value, setValue] = useState<T>(saved);

  // Reload when the server copy changes (save, sign, send back).
  useEffect(() => setValue(saved), [saved]);

  const dirty = JSON.stringify(value) !== JSON.stringify(saved);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const save = useMutation({
    mutationFn: () =>
      updatePrpDocument(doc.id, { content: value as unknown as Record<string, unknown>, version: doc.version }),
    onSuccess: (updated) => {
      qc.setQueryData(["prp", "document", doc.id], updated);
      qc.invalidateQueries({ queryKey: ["prp", "documents"] });
    },
    meta: mutationMeta.saved(PRP_DOC_LABELS[doc.doc_type]),
  });

  const patch = <K extends keyof T>(key: K, v: T[K]) => setValue((prev) => ({ ...prev, [key]: v }));

  return { value, setValue, patch, dirty, save };
}

/** Parse stored JSON onto defaults (shallow), tolerating bad/missing content. */
export function parseOnto<T extends object>(raw: string | undefined, defaults: T): T {
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw) as Partial<T>;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

/** Stable id for list rows (IRP goals keep theirs across reviews). */
export function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
